import IconButton from '@components/Buttons/IconButton'
import contextViewport, { ViewportTypeValues } from '@contexts/viewport'
import useBurgerMenuVisibility from '@hooks/useBurgerMenuVisibility'
import useNavigation from '@hooks/useNavigation'
import useToggleMenuVisibility from '@hooks/useToggleMenuVisibility'
import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'
import HeaderButtonsContainerContext from '@contexts/headerSearchBar'
import { FunctionComponent, useContext } from 'react'
import styled, { css, useTheme } from 'styled-components'
import CloseIcon from '../CloseIcon'
import SearchInput from '../SearchInput'
import Weather from '../Weather'
import FirstLevelItem from './FirstLevelItem'
import GDPRLink from './GDPRLink'

const { ViewportTypeContext } = contextViewport

interface NavigationProps {
  isVisible: boolean
}

const Navigation = styled.nav<NavigationProps>`
  ${({
    isVisible,
    theme: {
      colors: { white },
      animation: {
        transition: { default: defaultTransition },
      },
    },
  }) => css`
    z-index: 2;
    position: fixed;
    top: 0;
    bottom: 0;
    width: 413px;
    ${mobileCSS(css`
      width: calc(100% - 104px);
      min-width: 265px; // the logo should be fully visible on 375px screens like iphoneX
    `)}
    overflow-y: auto;
    background-color: ${white};
    padding-top: 0;
    right: 0;
    transition: ${defaultTransition};
    transform: translate3d(${isVisible ? 0 : '100%'}, 0, 0);
  `}
`

const MenuContainer = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-gap: 24px;
  margin-top: 24px;

  ${desktopCSS(css`
    margin-left: 8px;
  `)}
  ${mobileCSS(css`
    margin-top: 16px;
  `)}
`

const IconsContainer = styled.div`
  display: flex;
  margin: 0 24px;
  ${desktopCSS(css`
    justify-content: flex-end;
  `)}

  ${tabletCSS(css`
    justify-content: flex-end;
  `)}

  ${mobileCSS(css`
    justify-content: space-between;
  `)}
`

interface OverlayProps {
  isVisible: boolean
}

const Overlay = styled.div<OverlayProps>`
  ${({
    theme: {
      animation: {
        transition: { fast: fastTransition, default: defaultTransition },
      },
      colors: {
        grey: { grey_100 },
      },
    },
    isVisible,
  }) => css`
    position: absolute;
    top: 0;
    opacity: 0;
    transition: opacity ${fastTransition};
    width: 100vw;
    height: 100vh;
    left: -100vw;
    right: 0;
    bottom: 0;
    z-index: 1;
    background: ${grey_100};

    ${isVisible &&
    css`
      opacity: 0.3;
      left: 0;
      transition: opacity ${defaultTransition};
    `}
  `}
`

const SearchWrapper = styled.div`
  padding: 0 24px;
`

const StyledIconButton = styled(IconButton)`
  ${({
    theme: {
      colors: {
        brandColors: { brand_55 },
      },
    },
  }) => css`
    &:hover {
      svg {
        fill: ${brand_55};
      }
    }
  `}
`

const BurgerMenu: FunctionComponent = () => {
  const theme = useTheme()
  const navigationItems = useNavigation()
  const viewportType = useContext(ViewportTypeContext)
  const burgerMenuVisible = useBurgerMenuVisibility()
  const { openBurgerMenu, closeBurgerMenu } = useToggleMenuVisibility()
  const isMobile = viewportType === ViewportTypeValues.MOBILE

  return (
    <>
      <StyledIconButton
        ariaLabel="Menu"
        onClick={openBurgerMenu}
        iconName="burger-menu"
        iconSize={theme.measures.navigationBar.iconSize}
        iconColor={theme.colors.grey.grey_80}
      />
      <Navigation isVisible={burgerMenuVisible}>
        <MenuContainer>
          <IconsContainer>
            {isMobile && <Weather />}
            <CloseIcon onClick={() => closeBurgerMenu(false)} />
          </IconsContainer>
          {isMobile && (
            <SearchWrapper>
              <HeaderButtonsContainerContext.Provider
                value={{
                  isSearchBarCollapsed: false,
                  updateIsSearchBarCollapsed: () => {},
                }}>
                <SearchInput />
              </HeaderButtonsContainerContext.Provider>
            </SearchWrapper>
          )}
          <div>
            {navigationItems.map((item) => (
              <FirstLevelItem key={item.title} {...item} />
            ))}
          </div>
          <GDPRLink />
        </MenuContainer>
      </Navigation>
      <Overlay
        isVisible={burgerMenuVisible}
        onClick={() => closeBurgerMenu(false)}
      />
    </>
  )
}

export default BurgerMenu
